import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { 
  ShieldAlert, 
  FileText, 
  Settings, 
  LayoutDashboard, 
  ScanEye, 
  Menu, 
  X,
  Database,
  Users,
  LogOut,
  ClipboardCheck,
  TrendingUp,
  Timer,
  Info,
  PenLine,
  Mail,
  Search,
  UserSearch,
  Server
} from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Dashboard } from "@/components/Dashboard";
import { Trademarks } from "@/components/Trademarks";
import { Alerts } from "@/components/Alerts";
import { SettingsPage } from "@/components/Settings";
import { AlertDetail } from "@/components/AlertDetail";
import { TrademarkForm } from "@/components/TrademarkForm";
import { UKIPOImport } from "@/components/UKIPOImport";
import { Login } from "@/components/Login";
import { Register } from "@/components/Register";
import { ForgotPassword } from "@/components/ForgotPassword";
import { ResetPassword } from "@/components/ResetPassword";
import { UserManagement } from "@/components/UserManagement";
import { HealthReports } from "@/components/HealthReports";
import { StatusChanges } from "@/components/StatusChanges";
import { OppositionDeadlines } from "@/components/OppositionDeadlines";
import { LandingPage } from "@/components/LandingPage";
import { SignUp } from "@/components/SignUp";
import { TrademarkIntakeForm } from "@/components/TrademarkIntakeForm";
import { ConflictCheck } from "@/components/ConflictCheck";
import { MandatoryTwoFactorSetup } from "@/components/MandatoryTwoFactorSetup";
import { MultipleTMOwners } from "@/components/MultipleTMOwners";
import { BlogPage } from "@/components/BlogPage";
import { BlogPostPage } from "@/components/BlogPostPage";
import { BlogAdmin } from "@/components/BlogAdmin";
import { FAQPage } from "@/components/FAQPage";
import { AboutPage } from "@/components/AboutPage";
import { NewsletterAdmin } from "@/components/NewsletterAdmin";
import { SEODashboard } from "@/components/SEODashboard";
import { AllApplicants } from "@/components/AllApplicants";
import { SFTPTest } from "@/components/SFTPTest";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// NBR Logo URL
const NBR_LOGO = "https://nationalbusinessregister.co.uk/wp-content/uploads/2024/11/logo.png";

// Demo user email constant
const DEMO_USER_EMAIL = "demo@marksentinel.co.uk";

// Demo Mode Banner Component
const DemoModeBanner = () => (
  <div 
    className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-2 px-4 flex items-center justify-center gap-2 text-sm font-medium shadow-md"
    data-testid="demo-mode-banner"
  >
    <Info className="w-4 h-4" />
    <span>Demo Mode - You are viewing sample data for demonstration purposes</span>
  </div>
);

// Protected Route wrapper
const ProtectedRoute = ({ children, adminOnly = false, skip2FACheck = false }) => {
  const { isAuthenticated, loading, isAdmin, user } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse">
          <ScanEye className="w-12 h-12 text-slate-300" />
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/landing" replace />;
  }

  // Check if user needs to set up 2FA (unless we're already on the setup page)
  if (!skip2FACheck && user && !user.totp_enabled && location.pathname !== '/setup-2fa') {
    return <Navigate to="/setup-2fa" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Sidebar Navigation Component
const Sidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, logout } = useAuth();
  
  const navItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/trademarks", icon: FileText, label: "My Trademarks" },
    { path: "/ukipo", icon: Database, label: "UK IPO Data" },
    { path: "/multiple-owners", icon: Users, label: "Multiple TM Owners" },
    { path: "/all-applicants", icon: UserSearch, label: "All TM Applicants" },
    { path: "/status-changes", icon: TrendingUp, label: "Status Tracking" },
    { path: "/deadlines", icon: Timer, label: "Deadlines" },
    { path: "/alerts", icon: ShieldAlert, label: "Alerts" },
    { path: "/settings", icon: Settings, label: "Settings" },
  ];

  // Add admin-only nav items
  if (isAdmin) {
    navItems.push({ path: "/blog-admin", icon: PenLine, label: "Blog", adminOnly: true });
    navItems.push({ path: "/conflict-check", icon: ShieldAlert, label: "Conflict Check", adminOnly: true });
    navItems.push({ path: "/newsletter-admin", icon: Mail, label: "Newsletter", adminOnly: true });
    navItems.push({ path: "/seo-dashboard", icon: Search, label: "SEO Health", adminOnly: true });
    navItems.push({ path: "/sftp-test", icon: Server, label: "SFTP Import", adminOnly: true });
    navItems.push({ path: "/health-reports", icon: ClipboardCheck, label: "Health Reports", adminOnly: true });
    navItems.push({ path: "/users", icon: Users, label: "Users", adminOnly: true });
  }

  const handleLogout = () => {
    logout();
    navigate('/landing');
    onClose();
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={onClose}
          data-testid="sidebar-overlay"
        />
      )}
      
      <aside className={`sidebar ${isOpen ? 'open' : ''}`} data-testid="sidebar">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <img 
              src={NBR_LOGO} 
              alt="National Business Register" 
              className="h-10 w-auto"
            />
            <div>
              <h1 className="font-heading font-black text-lg tracking-tight text-slate-900">MARK</h1>
              <p className="text-xs text-teal-600 tracking-widest uppercase">Trade Mark Monitoring</p>
            </div>
          </div>
          <button 
            className="md:hidden p-2"
            onClick={onClose}
            data-testid="close-sidebar-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="sidebar-nav space-y-1" data-testid="sidebar-nav">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => { navigate(item.path); onClose(); }}
              className={`nav-item w-full text-left ${location.pathname === item.path ? 'active' : ''}`}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        {/* User info and logout - Fixed at bottom */}
        <div className="sidebar-footer space-y-3">
          <div className="swiss-card p-3">
            <div className="flex items-center gap-2 mb-1">
              <div className="status-dot active" />
              <span className="text-xs font-mono text-slate-500 uppercase">Logged in as</span>
            </div>
            <p className="text-sm font-medium text-slate-900 truncate">{user?.full_name || user?.email}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
          
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>
    </>
  );
};

// Main Layout
const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();
  
  // Check if current user is the demo user
  const isDemoUser = user?.email === DEMO_USER_EMAIL;

  return (
    <div className="dashboard-shell">
      <div className="noise-overlay" />
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      {/* Demo Mode Banner - shown when logged in as demo user */}
      {isDemoUser && (
        <div className="fixed top-0 left-0 right-0 z-50 md:left-[240px]">
          <DemoModeBanner />
        </div>
      )}
      
      {/* Mobile header */}
      <div className={`md:hidden fixed left-0 right-0 h-16 bg-white border-b border-slate-200 z-20 flex items-center px-4 ${isDemoUser ? 'top-10' : 'top-0'}`}>
        <button 
          onClick={() => setSidebarOpen(true)}
          className="p-2"
          data-testid="open-sidebar-btn"
        >
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2 ml-4">
          <img 
            src={NBR_LOGO} 
            alt="National Business Register" 
            className="h-8 w-auto"
          />
          <span className="font-heading font-black text-slate-900">NBR MONITORING</span>
        </div>
      </div>

      <main className={`main-content ${isDemoUser ? 'pt-26 md:pt-10' : 'pt-16 md:pt-0'}`}>
        <div className="p-6 md:p-8 lg:p-12">
          {children}
        </div>
      </main>
    </div>
  );
};

// Phase 1: Public website only. Platform access via hidden /admin-access URL.
const PLATFORM_ENABLED = false;

// App Routes Component
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public website routes - always available */}
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/register-trademarks" element={<TrademarkIntakeForm />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/signup-success" element={<SignUp />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/about" element={<AboutPage />} />

      {/* Root URL goes to landing page */}
      <Route path="/" element={<Navigate to="/landing" replace />} />

      {/* Hidden admin access - only those who know the URL can reach the platform */}
      <Route path="/admin-access" element={<Login />} />
      <Route path="/register" element={PLATFORM_ENABLED ? <Register /> : <Navigate to="/landing" replace />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Mandatory 2FA setup route - requires authentication but no 2FA */}
      <Route path="/setup-2fa" element={
        <ProtectedRoute skip2FACheck={true}>
          <MandatoryTwoFactorSetup />
        </ProtectedRoute>
      } />
      
      {/* Protected platform routes - only accessible when logged in via /admin-access */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout><Dashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/trademarks" element={
        <ProtectedRoute>
          <Layout><Trademarks /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/trademarks/new" element={
        <ProtectedRoute>
          <Layout><TrademarkForm /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/trademarks/edit/:id" element={
        <ProtectedRoute>
          <Layout><TrademarkForm /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/ukipo" element={
        <ProtectedRoute>
          <Layout><UKIPOImport /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/multiple-owners" element={
        <ProtectedRoute>
          <Layout><MultipleTMOwners /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/all-applicants" element={
        <ProtectedRoute>
          <Layout><AllApplicants /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/status-changes" element={
        <ProtectedRoute>
          <Layout><StatusChanges /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/deadlines" element={
        <ProtectedRoute>
          <Layout><OppositionDeadlines /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/alerts" element={
        <ProtectedRoute>
          <Layout><Alerts /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/alerts/:id" element={
        <ProtectedRoute>
          <Layout><AlertDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout><SettingsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/health-reports" element={
        <ProtectedRoute adminOnly={true}>
          <Layout><HealthReports /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute adminOnly={true}>
          <Layout><UserManagement /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/conflict-check" element={
        <ProtectedRoute adminOnly={true}>
          <Layout><ConflictCheck /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/blog-admin" element={
        <ProtectedRoute adminOnly={true}>
          <Layout><BlogAdmin /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/newsletter-admin" element={
        <ProtectedRoute adminOnly={true}>
          <Layout><NewsletterAdmin /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/seo-dashboard" element={
        <ProtectedRoute adminOnly={true}>
          <Layout><SEODashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/sftp-test" element={
        <ProtectedRoute adminOnly={true}>
          <Layout><SFTPTest /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Login redirect - hide from public, redirect to landing */}
      <Route path="/login" element={<Navigate to="/landing" replace />} />
      
      {/* Catch all - redirect to landing page */}
      <Route path="*" element={<Navigate to="/landing" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
